import { createSelector } from '@reduxjs/toolkit'
import { AllowedRefs, convertToRefString } from 'lib/websocket-notify'
import { RootState } from 'store/store'
import { MultiGetRootState, MultiGetSlice } from './multigetSlice'

// root selector
export const selectMultiGetRoot = (state: RootState): MultiGetRootState<unknown> => {
    return state.multiGet || {}
}

// slice selector factory
export const makeSelectSlice = (type: string): ((state: RootState) => MultiGetSlice<unknown> | undefined) => {
    return createSelector(selectMultiGetRoot, (root) => root[type] as MultiGetSlice<unknown> | undefined)
}

interface SelectorResult {
    data: unknown
    isLoading: boolean
    isError: boolean
    isStale?: boolean
    error?: unknown
}

const empty: SelectorResult = { data: undefined, isLoading: false, isError: false }

/**
 * Creates a memoized selector for a specific object by ref.
 *
 * @param sliceKey - The cache key for the Redux slice (format: "{tag}:{serviceName}.{methodName}")
 * @param ref - The ref object to look up
 */
export const makeSelectObjectByRef = <R extends AllowedRefs>(
    sliceKey: string,
    ref: R,
): ((state: RootState) => SelectorResult) => {
    // Extract tag from sliceKey format "{tag}:{serviceName}.{methodName}"
    const tag = sliceKey.split(':')[0]
    return createSelector(makeSelectSlice(sliceKey), (slice): SelectorResult => {
        const key = convertToRefString(ref, tag)
        if (!slice || !key) return empty

        return {
            data: slice.byId[key],
            isLoading: slice.status[key] === 'loading',
            isError: slice.status[key] === 'error',
            isStale: slice.status[key] === 'stale',
            error: slice.error[key],
        }
    })
}
